import { TaJson } from "ta-json";

import { Nullable, Object_Unknown } from "../base-types";
import { NOTIFICATIONS } from "../constants";
import Guard from "../guard";
import Link from "../link";
import { NotificationMapper } from "../mappers/notification-mapper";
import { DistributeNotificationResource } from "../models/notifications/distribute-notification-resource";
import { MailRequest } from "../models/notifications/mail-request";
import { MailRequestBroadcast } from "../models/notifications/mail-request-broadcast";
import { MailRequestById } from "../models/notifications/mail-request-by-id";
import { MailRequestByUsername } from "../models/notifications/mail-request-by-username";
import { RealtimeRequest } from "../models/notifications/realtime-request";
import { RealtimeRequestBroadcast } from "../models/notifications/realtime-request-broadcast";
import { RealtimeRequestById } from "../models/notifications/realtime-request-by-id";
import { RealtimeRequestByUsername } from "../models/notifications/realtime-request-by-username";
import { TypeGuards } from "../type-guards";
import { IExtendedContentHubClient } from "./extended-client";
import { ResponseHandler } from "./response-handler";

export interface INotificationsClient {
    /**
     * Sends a registration confirmation e-mail to the specified users by id.
     * This action requires the 'SendConfirmationEmail' privilege.
     * @param usernames - Usernames or ids that will receive the registration confirmation e-mail
     */
    sendConfirmationEmailAsync(usernames: Array<string> | Array<number>): Promise<void>;

    /**
     * Sends an e-mail to users by user id, username or to all users.
     * @param mailRequest - must have type MailRequestById | MailRequestByUsername | MailRequestBroadcast
     */
    sendEmailNotificationAsync(
        mailRequest: MailRequestById | MailRequestByUsername | MailRequestBroadcast
    ): Promise<void>;

    /**
     * Sends a real-time notification (browser notification) to users by user id, username or to all users.
     * @param realtimeRequest - must have type RealtimeRequestById | RealtimeRequestByUsername | RealtimeRequestBroadcast
     */
    sendRealTimeNotificationAsync(
        realtimeRequest: RealtimeRequestById | RealtimeRequestByUsername | RealtimeRequestBroadcast
    ): Promise<void>;
}

export class NotificationsClient implements INotificationsClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);

        this._client = client;
    }

    //! Not necessary for now, can be added later (lots of boilerplate)
    // // public async getMailTemplateAsync(name: string, loadConfiguration: Nullable<IEntityLoadConfiguration> = null)

    async sendConfirmationEmailAsync(users: Array<string> | Array<number>): Promise<void> {
        let userIds: Array<number> = [];

        if (TypeGuards.isStringArray(users)) {
            userIds = (await this._client.users.getUserIdsAsync(users)) as Array<number>;
        } else {
            userIds = users;
        }
        Guard.validIds(userIds);

        for (const userId of userIds) {
            const link = await this._client.linkHelper.sendConfirmationMailToLinkAsync(userId);
            const response = await this._client.raw.postAsync(link.href, TaJson.serialize(userId));

            ResponseHandler.handleErrors(response);
        }
    }

    async sendEmailNotificationAsync(
        mailRequest: MailRequestById | MailRequestByUsername | MailRequestBroadcast
    ): Promise<void> {
        Guard.notNullOrUndefined(mailRequest);

        mailRequest.validate();

        if ("recipients" in mailRequest) {
            if (TypeGuards.isStringArray(mailRequest.recipients)) {
                // Usernames
                const mailUsernames = mailRequest as MailRequestByUsername;

                const ids = await this._client.users.getUserIdsAsync(mailUsernames.recipients);
                if (ids !== null) {
                    await this.sendPrivateEmailNotificationAsync(mailUsernames, ids);
                }
            } else {
                // Ids
                const mailIds = mailRequest as MailRequestById;
                await this.sendPrivateEmailNotificationAsync(mailIds, mailIds.recipients);
            }
        } else {
            // Broadcast
            await this.sendPrivateEmailNotificationAsync(mailRequest, null);
        }
    }

    async sendRealTimeNotificationAsync(
        realtimeRequest: RealtimeRequestById | RealtimeRequestByUsername | RealtimeRequestBroadcast
    ): Promise<void> {
        Guard.notNull(realtimeRequest);

        realtimeRequest.validate();

        if ("recipients" in realtimeRequest) {
            if (TypeGuards.isStringArray(realtimeRequest.recipients)) {
                // Usernames
                const realtimeUsers = realtimeRequest as RealtimeRequestByUsername;
                const ids = await this._client.users.getUserIdsAsync(realtimeUsers.recipients);
                if (ids !== null) {
                    await this.sendPrivateRealTimeNotificationAsync(realtimeRequest, ids);
                }
            } else {
                // Ids
                const realtimeIds = realtimeRequest as RealtimeRequestById;
                await this.sendPrivateRealTimeNotificationAsync(realtimeIds, realtimeIds.recipients);
            }
        } else {
            // Broadcast
            await this.sendPrivateRealTimeNotificationAsync(realtimeRequest, null);
        }
    }

    //#region Private functions

    private async sendPrivateEmailNotificationAsync(
        mailRequest: MailRequest,
        recipients: Nullable<Array<number>>
    ): Promise<void> {
        return this.sendNotificationAsync(
            mailRequest.mailTemplateName,
            recipients,
            "EmailNotification",
            mailRequest.variables
        );
    }

    private async sendPrivateRealTimeNotificationAsync(
        realtimeRequest: RealtimeRequest,
        recipients: Nullable<Array<number>>
    ): Promise<void> {
        const parameters = NotificationMapper.mapParameters(realtimeRequest);

        return this.sendNotificationAsync(NOTIFICATIONS.REALTIME.raw, recipients, "RealTimeNotification", parameters);
    }

    private async sendNotificationAsync(
        notificationName: string,
        recipients: Nullable<Array<number>>,
        channel: string,
        parameters: Object_Unknown
    ): Promise<void> {
        const link = await this._client.linkHelper.sendNotificationToLinkAsync(notificationName);

        // Get user links.
        let userLinks: Nullable<Array<Link>> = null;
        const linkPromises = recipients?.map(id => this._client.linkHelper.entityToLinkAsync(id));
        if (linkPromises != null) {
            userLinks = await Promise.all(linkPromises);
        }

        // Create the resource.
        const resource = new DistributeNotificationResource();
        resource.recipients = userLinks;
        resource.channels = [channel];
        resource.parameters = parameters;

        const response = await this._client.raw.postAsync(link.href, TaJson.serialize(resource));

        ResponseHandler.handleErrors(response);
    }

    //#endregion
}
